import { nextTick, ref, type Ref } from 'vue'
import { normalizeLocationTag } from '@/utils/checklistLocations'

type LocationsSource = string[] | Ref<string[]> | (() => string[])

function resolveLocations(source: LocationsSource): string[] {
  if (typeof source === 'function') return source()
  if (Array.isArray(source)) return source
  return source.value
}

/**
 * Location 标签输入：回车成灰标签，左右键切换焦点，退格删除。
 * 支持传入 reactive 数组 / ref / getter，避免编辑页整表替换数组后失效。
 */
export function useLocationTagInput(source: LocationsSource, onChange?: () => void) {
  const locationDraft = ref('')
  const locationInputRef = ref<HTMLInputElement | null>(null)
  const locationContainerRef = ref<HTMLElement | null>(null)
  const focusedTagIndex = ref<number | null>(null)

  function list(): string[] {
    return resolveLocations(source)
  }

  function getTagElements(): HTMLElement[] {
    const root = locationContainerRef.value
    if (!root) return []
    return Array.from(root.querySelectorAll<HTMLElement>('.location-tag'))
  }

  function focusTag(index: number): void {
    const locations = list()
    if (index < 0 || index >= locations.length) {
      focusedTagIndex.value = null
      locationInputRef.value?.focus()
      return
    }
    focusedTagIndex.value = index
    nextTick(() => {
      const el = getTagElements()[index]
      el?.focus()
    })
  }

  function focusLocationInput(): void {
    focusedTagIndex.value = null
    nextTick(() => {
      locationInputRef.value?.focus()
    })
  }

  function addLocationTag(raw: string): void {
    const tag = normalizeLocationTag(raw)
    if (!tag) return
    const locations = list()
    if (locations.some((item) => item.toLowerCase() === tag.toLowerCase())) {
      locationDraft.value = ''
      return
    }
    locations.push(tag)
    locationDraft.value = ''
    focusedTagIndex.value = null
    onChange?.()
  }

  function removeLocationTag(index: number): void {
    const locations = list()
    if (index < 0 || index >= locations.length) return
    locations.splice(index, 1)
    onChange?.()
    if (locations.length === 0) {
      focusedTagIndex.value = null
      focusLocationInput()
      return
    }
    focusTag(Math.min(index, locations.length - 1))
  }

  function commitLocationDraft(): void {
    if (!locationDraft.value.trim()) return
    addLocationTag(locationDraft.value)
  }

  function handleLocationKeydown(event: KeyboardEvent): void {
    const locations = list()
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault()
      commitLocationDraft()
      return
    }

    const input = event.target as HTMLInputElement
    const cursorAtStart = (input.selectionStart ?? 0) === 0 && (input.selectionEnd ?? 0) === 0

    if (event.key === 'ArrowLeft' && cursorAtStart && locations.length > 0) {
      event.preventDefault()
      focusTag(locations.length - 1)
      return
    }

    if (event.key === 'Backspace' && !locationDraft.value && locations.length > 0) {
      event.preventDefault()
      removeLocationTag(locations.length - 1)
    }
  }

  function handleTagKeydown(event: KeyboardEvent, index: number): void {
    const locations = list()
    if (event.key === 'ArrowLeft') {
      event.preventDefault()
      if (index > 0) focusTag(index - 1)
      return
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault()
      if (index < locations.length - 1) {
        focusTag(index + 1)
      } else {
        focusLocationInput()
      }
      return
    }
    if (event.key === 'Backspace' || event.key === 'Delete') {
      event.preventDefault()
      removeLocationTag(index)
      return
    }
    if (event.key === 'Enter') {
      event.preventDefault()
      focusLocationInput()
    }
  }

  function handleLocationBlur(): void {
    window.setTimeout(() => {
      const active = document.activeElement
      const tags = getTagElements()
      const inside = active === locationInputRef.value || tags.some((el) => el === active)
      if (!inside) {
        commitLocationDraft()
        onChange?.()
        focusedTagIndex.value = null
      }
    }, 0)
  }

  function onTagClick(index: number): void {
    focusTag(index)
  }

  return {
    locationDraft,
    locationInputRef,
    locationContainerRef,
    focusedTagIndex,
    focusLocationInput,
    commitLocationDraft,
    handleLocationKeydown,
    handleTagKeydown,
    handleLocationBlur,
    onTagClick,
  }
}
